import axios from 'axios'
import { API_BASE } from '../utils/constants'

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err),
)

// ── Public model endpoints ──────────────────────────────
export function fetchModels(caseId) {
  return api.get(`/models/${caseId}`)
}

export function getModelFileUrl(modelId) {
  return `${API_BASE}/api/models/file/${modelId}`
}

export async function getModelSignedUrl(modelId) {
  const res = await api.get(`/models/url/${modelId}`)
  return res.data.url
}

// ── Case validation ─────────────────────────────────────
export function validateCaseId(caseId, viewerPin) {
  return api.post('/cases/validate', { caseId, viewerPin })
}

// ── Approval ─────────────────────────────────────────────
export function getApprovalStatus(caseId, pin) {
  return api.get(`/cases/${caseId}/approval-status`, { params: { pin } })
}

export function submitApproval(caseId, { pin, contactId, status, comment }) {
  return api.post(`/cases/${caseId}/approve`, { pin, contactId, status, comment })
}

// ── Annotations ─────────────────────────────────────────
export function fetchAnnotations(caseId, modelId) {
  return api.get(`/models/${caseId}/annotations`, { params: modelId ? { modelId } : {} })
}

// Threshold at which we switch from single-request to chunked upload.
// Cloud Run's HTTP/1.1 body limit is 32 MiB — staying safely under it.
const ANNOTATION_SINGLE_LIMIT = 25 * 1024 * 1024
const ANNOTATION_CHUNK_SIZE = 20 * 1024 * 1024 // must match backend

export async function saveAnnotation(caseId, formData) {
  const glbBlob = formData.get('annotatedGlb')
  const glbSize = glbBlob?.size || 0

  // Small (or no) GLB: existing single-request path
  if (glbSize <= ANNOTATION_SINGLE_LIMIT) {
    return api.post(`/models/${caseId}/annotations`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    })
  }

  // Large GLB: chunked path (bypasses Cloud Run 32 MiB single-body limit)
  const totalChunks = Math.ceil(glbSize / ANNOTATION_CHUNK_SIZE)
  const { data: { uploadId } } = await api.post(`/models/${caseId}/annotations/upload/init`, {
    modelId: formData.get('modelId'),
    authorName: formData.get('authorName'),
    contactId: formData.get('contactId') || null,
    pinLabels: formData.get('pinLabels') || null,
    brushRegions: formData.get('brushRegions') || null,
    notes: formData.get('notes') || null,
    filename: 'annotated.glb',
    totalChunks,
    fileSize: glbSize,
  })

  for (let i = 0; i < totalChunks; i++) {
    const start = i * ANNOTATION_CHUNK_SIZE
    const end = Math.min(start + ANNOTATION_CHUNK_SIZE, glbSize)
    const chunk = glbBlob.slice(start, end)
    const chunkForm = new FormData()
    chunkForm.append('chunk', chunk, `chunk-${i}`)
    const res = await api.post(`/models/annotations/upload/chunk/${uploadId}?index=${i}`, chunkForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    })
    if (res.status === 201) return res // last chunk returns the annotation
  }
}

export function getAnnotationFileUrl(annotationId) {
  return `${API_BASE}/api/models/annotation/${annotationId}/file`
}

// ── Model Approval (surgeon approves from viewer) ────────
export function approveModel(caseId, { viewerPin, authorName, contactId, notes }) {
  return api.post(`/models/${caseId}/approve`, { viewerPin, authorName, contactId, notes })
}

export default api
